"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { VennDiagram } from "@/components/tools/algebra/venn-diagram";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  cartesianProduct,
  formatSet,
  MAX_POWER_SET_LISTED,
  MAX_PRODUCT_LISTED,
  parseSet,
  powerSet,
  powerSetSize,
  reportSets,
  type SetReport,
} from "@/lib/tool-kit/algebra/sets";

/** English strings. A translated content file overrides these through `ui`. */
export const SET_CALCULATOR_STRINGS = {
  setA: "Set A",
  setB: "Set B",
  setHint:
    "Separate elements with commas, spaces or new lines. Braces are optional: {1, 2, 3}, 1 2 3 and red, green all work. Numbers and words are both fine.",
  universe: "Universal set U (optional)",
  universeHint:
    "Needed for complements. If you leave it empty, U is taken to be A ∪ B.",
  examplesLabel: "Try an example",
  exampleLabels: ["Numbers", "Words", "Subset", "Disjoint"],
  exampleA: ["1, 2, 3, 4, 5", "red, green, blue", "2, 4", "a, b"],
  exampleB: ["4, 5, 6, 7", "green, yellow", "1, 2, 3, 4, 5, 6", "c, d"],
  exampleU: ["", "", "1, 2, 3, 4, 5, 6, 7, 8, 9, 10", ""],
  results: "Results",
  sizeA: "|A|",
  sizeB: "|B|",
  sizeUnion: "|A ∪ B|",
  sizeIntersection: "|A ∩ B|",
  duplicates: "one: duplicate entry ignored|other: duplicate entries ignored",
  outsideWarning:
    "These elements of A or B are not in U, so they are shown outside it: {elements}.",
  relations: {
    equal: "A and B are equal: they have exactly the same elements (A = B).",
    "a-proper-subset":
      "A is a proper subset of B (A ⊂ B): every element of A is in B, and B has more.",
    "b-proper-subset":
      "B is a proper subset of A (B ⊂ A): every element of B is in A, and A has more.",
    disjoint:
      "A and B are disjoint: they have no element in common (A ∩ B = ∅).",
    overlap: "A and B overlap, and neither is a subset of the other.",
  },
  inclusionExclusion:
    "Inclusion–exclusion: |A ∪ B| = |A| + |B| − |A ∩ B| = {a} + {b} − {c} = {d}",
  tableHeading: "Set operations",
  operation: "Operation",
  result: "Result",
  elements: "Elements",
  rows: {
    union: "Union, A ∪ B",
    intersection: "Intersection, A ∩ B",
    aMinusB: "Difference, A − B",
    bMinusA: "Difference, B − A",
    symmetric: "Symmetric difference, A Δ B",
    complementA: "Complement of A, A′ = U − A",
    complementB: "Complement of B, B′ = U − B",
  },
  vennHeading: "Venn diagram",
  vennTitle:
    "Venn diagram of A and B with the elements of A only, both, and B only written in each region",
  downloadPng: "Download diagram (PNG)",
  productHeading: "Cartesian product A × B",
  productSize:
    "|A × B| = |A| × |B| = {a} × {b} = {n}. B × A has the same size but its pairs are written the other way round.",
  productCapped: "Showing the first {n} pairs.",
  powerHeading: "Power sets",
  powerSizeLine:
    "A set with n elements has 2ⁿ subsets, including ∅ and the set itself. |P(A)| = 2^{n} = {size}.",
  powerCapped:
    "The list is shown only for sets of up to {max} elements; this one has {n}.",
  powerOf: "P({set})",
  copy: "Copy results",
  copied: "Copied!",
  empty: "Both sets are empty. Enter some elements to see the operations.",
  enter: "Enter elements for set A and set B.",
} as const;

type Strings = typeof SET_CALCULATOR_STRINGS;

function resultRows(
  report: SetReport,
  strings: Strings
): [string, string, number][] {
  const row = (label: string, elements: string[]): [string, string, number] => [
    label,
    formatSet(elements),
    elements.length,
  ];
  return [
    row(strings.rows.union, report.union),
    row(strings.rows.intersection, report.intersection),
    row(strings.rows.aMinusB, report.aMinusB),
    row(strings.rows.bMinusA, report.bMinusA),
    row(strings.rows.symmetric, report.symmetric),
    row(strings.rows.complementA, report.complementA),
    row(strings.rows.complementB, report.complementB),
  ];
}

function PowerSection({
  name,
  elements,
  strings,
}: {
  name: string;
  elements: string[];
  strings: Strings;
}) {
  const n = elements.length;
  const listed = n <= MAX_POWER_SET_LISTED;
  return (
    <div className="space-y-1 text-sm">
      <p className="font-medium text-foreground">
        {t(strings.powerOf, { set: name })}
      </p>
      <p className="text-muted-foreground">
        {t(strings.powerSizeLine, { n, size: powerSetSize(n).toString() })}
      </p>
      {listed ? (
        <p className="break-words font-mono text-foreground">
          {`{${powerSet(elements).map(formatSet).join(", ")}}`}
        </p>
      ) : (
        <p className="text-muted-foreground">
          {t(strings.powerCapped, { max: MAX_POWER_SET_LISTED, n })}
        </p>
      )}
    </div>
  );
}

function Results({
  aText,
  bText,
  uText,
  strings,
}: {
  aText: string;
  bText: string;
  uText: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const a = parseSet(aText);
  const b = parseSet(bText);
  const u = uText.trim() === "" ? null : parseSet(uText);
  if (a.elements.length === 0 && b.elements.length === 0) {
    return <Notice>{strings.empty}</Notice>;
  }
  const report = reportSets(a.elements, b.elements, u ? u.elements : null);
  const rows = resultRows(report, strings);
  const duplicates = a.duplicates + b.duplicates;
  const product = cartesianProduct(a.elements, b.elements);
  const total = a.elements.length * b.elements.length;
  const text = rows.map(([label, value]) => `${label}: ${value}`).join("\n");
  return (
    <>
      <StatGrid>
        <StatCard label={strings.sizeA} value={fmt(a.elements.length)} />
        <StatCard label={strings.sizeB} value={fmt(b.elements.length)} />
        <StatCard
          emphasis
          label={strings.sizeUnion}
          value={fmt(report.union.length)}
        />
        <StatCard
          label={strings.sizeIntersection}
          value={fmt(report.intersection.length)}
        />
      </StatGrid>
      {duplicates > 0 ? (
        <p className="text-muted-foreground text-sm">
          {fmt(duplicates)}
          {tPlural(strings.duplicates, locale, duplicates)}
        </p>
      ) : null}
      {report.outside.length > 0 ? (
        <Notice>
          {t(strings.outsideWarning, { elements: report.outside.join(", ") })}
        </Notice>
      ) : null}
      <div className={`${RESULT_CARD} space-y-2 text-sm`}>
        <p className="text-foreground">{strings.relations[report.relation]}</p>
        <p className="font-mono text-muted-foreground">
          {t(strings.inclusionExclusion, {
            a: report.a.length,
            b: report.b.length,
            c: report.intersection.length,
            d: report.union.length,
          })}
        </p>
      </div>
      <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
      <DataTable
        head={[strings.operation, strings.result, strings.elements]}
        rows={rows.map(([label, value, count]) => [label, value, fmt(count)])}
      />
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={text}
      />
      <h3 className={WIDGET_H3}>{strings.vennHeading}</h3>
      <VennDiagram
        both={report.intersection}
        downloadLabel={strings.downloadPng}
        onlyA={report.aMinusB}
        onlyB={report.bMinusA}
        outside={
          u
            ? report.complementA.filter((x) => report.complementB.includes(x))
            : []
        }
        showUniverse={u !== null}
        title={strings.vennTitle}
      />
      <h3 className={WIDGET_H3}>{strings.productHeading}</h3>
      <p className="text-muted-foreground text-sm">
        {t(strings.productSize, {
          a: a.elements.length,
          b: b.elements.length,
          n: total,
        })}
      </p>
      {product.length > 0 ? (
        <p className="break-words font-mono text-foreground text-sm">
          {`{${product.join(", ")}${total > product.length ? ", …" : ""}}`}
        </p>
      ) : null}
      {total > MAX_PRODUCT_LISTED ? (
        <p className="text-muted-foreground text-xs">
          {t(strings.productCapped, { n: MAX_PRODUCT_LISTED })}
        </p>
      ) : null}
      <h3 className={WIDGET_H3}>{strings.powerHeading}</h3>
      <PowerSection elements={a.elements} name="A" strings={strings} />
      <PowerSection elements={b.elements} name="B" strings={strings} />
    </>
  );
}

export default function SetCalculatorWidget() {
  const strings = useToolUi(SET_CALCULATOR_STRINGS);
  const [a, setA] = useState<string>(strings.exampleA[0]);
  const [b, setB] = useState<string>(strings.exampleB[0]);
  const [u, setU] = useState<string>(strings.exampleU[0]);
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextAreaField
          hint={strings.setHint}
          id="set-a"
          label={strings.setA}
          onChange={setA}
          rows={3}
          value={a}
        />
        <TextAreaField
          id="set-b"
          label={strings.setB}
          onChange={setB}
          rows={3}
          value={b}
        />
      </div>
      <TextField
        hint={strings.universeHint}
        id="set-u"
        label={strings.universe}
        mono
        onChange={setU}
        value={u}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted-foreground text-sm">
          {strings.examplesLabel}
        </span>
        {strings.exampleLabels.map((label, i) => (
          <button
            className={BUTTON_SMALL}
            key={label}
            onClick={() => {
              setA(strings.exampleA[i] ?? "");
              setB(strings.exampleB[i] ?? "");
              setU(strings.exampleU[i] ?? "");
            }}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {a.trim() === "" && b.trim() === "" ? (
          <Notice>{strings.enter}</Notice>
        ) : (
          <Results aText={a} bText={b} strings={strings} uText={u} />
        )}
      </section>
    </>
  );
}
